// Библиотека источников (4.13, Прил. М): формы по видам изданий, строка по ГОСТ Р 7.0.100–2018 с сервера
// (/api/sources/format), подсказки о недостающих сведениях, импорт BibTeX / RIS / готового списка
// (/api/sources/parse), номера по алфавиту (сервис нумерации), ссылки [N] и [N, с. 5] (4.14).
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Ctx, DialogProps } from '../../core/registry';
import type { Source } from '../../core/types';
import { appStore, useApp } from '../../core/store';
import { useNumbering } from '../../core/editor/numberingPlugin';
import { newId } from '../../core/schema/util';
import { Btn, Field, Modal } from '../../ui/controls';
import { Icon } from '../../ui/icons';
import { todayRu, NB } from './data';

type AnySource = Source & Record<string, any>;

export const KINDS: [string, string, string][] = [
  ['book', 'Книга, учебник', 'book'], ['article', 'Статья в журнале', 'file'], ['chapter', 'Статья в сборнике, глава', 'book'],
  ['web', 'Сайт, электронный ресурс', 'external'], ['standard', 'ГОСТ, стандарт', 'file-check'], ['law', 'Закон, нормативный акт', 'glossary'],
  ['thesis', 'Диссертация, ВКР', 'student'], ['patent', 'Патент', 'title'], ['software', 'Программа для ЭВМ', 'braces'],
  ['other', 'Готовая строка', 'text'],
];

type F = [string, string, string?];     // поле, подпись, пример
const PUB: F[] = [['city', 'Место издания', 'Москва'], ['publisher', 'Издательство', 'Питер'], ['year', 'Год', '2024']];
const NET: F[] = [['url', 'URL (если электронный)', 'https://…'], ['accessed', 'Дата обращения', 'ДД.ММ.ГГГГ']];
const FIELDS: Record<string, F[]> = {
  book: [['title', 'Заглавие', 'Компьютерные сети'], ['subtitle', 'Сведения к заглавию', 'учебник для вузов'], ['resp', 'Прочая ответственность', 'под ред. И. И. Иванова'],
    ['edition', 'Издание', '5-е изд., перераб. и доп.'], ...PUB, ['pages', 'Объём (страниц)', '992'], ['isbn', 'ISBN', '978-5-…'], ...NET],
  article: [['title', 'Заглавие статьи', ''], ['journal', 'Журнал', 'Вестник САФУ'], ['year', 'Год', '2024'], ['volume', 'Том', '5'], ['issue', 'Номер (выпуск)', '3'],
    ['pagesRange', 'Страницы', '12–15'], ['doi', 'DOI', '10.xxxx/…'], ...NET],
  chapter: [['title', 'Заглавие статьи/главы', ''], ['journal', 'Сборник, книга (после «//»)', 'Материалы конференции …'], ['edition', 'Издание', ''], ...PUB,
    ['pagesRange', 'Страницы', '228–255'], ['doi', 'DOI', ''], ...NET],
  web: [['title', 'Название страницы / ресурса', 'Документация Python'], ['journal', 'Название сайта (после «//»)', 'Python.org'], ['subtitle', 'Сведения к заглавию', ''],
    ['publisher', 'Владелец сайта', ''], ['year', 'Год', ''], ['url', 'URL', 'https://…'], ['accessed', 'Дата обращения', 'ДД.ММ.ГГГГ']],
  standard: [['number', 'Обозначение', 'ГОСТ Р 7.0.100–2018'], ['title', 'Название', 'Библиографическая запись. Библиографическое описание…'],
    ['subtitle', 'Сведения', 'национальный стандарт Российской Федерации'], ['date', 'Дата введения', '2019-07-01'], ...PUB, ['pages', 'Объём', '70'], ...NET],
  law: [['issuer', 'Заголовок (кем принят)', 'Российская Федерация. Законы'], ['title', 'Название акта', 'Об образовании в Российской Федерации'],
    ['subtitle', 'Вид и номер', 'федер. закон от 29.12.2012 № 273-ФЗ'], ['adopted', 'Сведения о принятии', 'принят Гос. Думой 21 дек. 2012 г.'],
    ['journal', 'Где опубликован', 'Собрание законодательства РФ'], ['year', 'Год', ''], ['issue', 'Номер', ''], ['article', 'Статья', '7598'],
    // если закон издан отдельной книгой (Прил. М.1: «– Москва: Проспект, 2015. – 160 с.»)
    ['city', 'Место (если издан книгой)', 'Москва'], ['publisher', 'Издательство (если издан книгой)', 'Проспект'], ['pages', 'Объём (если издан книгой)', '160'], ...NET],
  thesis: [['title', 'Тема', ''], ['subtitle', 'Вид работы', 'дис. … канд. техн. наук : 05.13.01'], ['city', 'Место', 'Архангельск'], ['year', 'Год', ''], ['pages', 'Объём', ''], ...NET],
  patent: [['number', 'Номер и код', '2602478 C1'], ['country', 'Страна', 'Российская Федерация'], ['ipc', 'МПК', 'F16B 39/02'], ['title', 'Название изобретения', ''],
    ['appNumber', 'Номер заявки', '2015124119/11'], ['filed', 'Заявлено', 'ДД.ММ.ГГГГ'], ['published', 'Опубликовано', 'ДД.ММ.ГГГГ'],
    ['applicant', 'Заявитель / патентообладатель', ''], ['issue', 'Бюллетень №', '32'], ['pages', 'Объём', ''], ...NET],
  software: [['title', 'Название программы', ''], ['subtitle', 'Сведения', 'программа для ЭВМ'], ['edition', 'Версия', 'Версия 2.1'], ...PUB, ...NET],
  other: [],
};
const WIDE = new Set(['title', 'subtitle', 'journal', 'url', 'resp', 'issuer', 'adopted', 'applicant', 'number']);
const NO_AUTHORS = new Set(['other', 'standard', 'law']);

// ------------------------------------------------------------------------------------------------ строки ГОСТ
/** Подсказки сервера о недостающих сведениях: id -> [строки]. */
export const sourceProblems = new Map<string, string[]>();

let fmtTimer: number | undefined;
export function scheduleFormat(ctx: Ctx) {
  window.clearTimeout(fmtTimer);
  fmtTimer = window.setTimeout(async () => {
    const s = appStore.getState();
    if (!s.sources.length || !s.meta) { sourceProblems.clear(); return; }
    try {
      const r = await ctx.api.post<{ items: { id: string; text: string; problems: string[] }[] }>('/api/sources/format',
        { sources: s.sources, order: s.meta.options.sources_order });
      sourceProblems.clear();
      r.items.forEach((x) => { if (x.problems?.length) sourceProblems.set(x.id, x.problems); });
      const byId = new Map(r.items.map((x) => [x.id, x.text]));
      const cur = appStore.getState().sources;
      if (cur.some((x) => byId.has(x.id) && x.formatted !== byId.get(x.id))) {
        appStore.getState().setSources((list) => list.map((x) => (byId.has(x.id) ? { ...x, formatted: byId.get(x.id) } : x)));
      }
    } catch { /* сервер недоступен - покажем без форматирования */ }
  }, 400);
}

function srcText(s: Source) { return s.text || s.formatted || s.title || s.id; }
const normTitle = (t?: string) => (t || '').toLowerCase().replace(/ё/g, 'е').replace(/[^\p{L}\p{N}]+/gu, '');

export function insertCite(ctx: Ctx, items: { source: string; loc?: string }[]) {
  ctx.editor?.chain().focus().insertContent({ type: 'cite', attrs: { items } }).run();
}

// ------------------------------------------------------------------------------------------------ панель
export function SourcesPanel({ ctx }: { ctx: Ctx }) {
  const sources = useApp((s) => s.sources);
  const order = useApp((s) => s.meta?.options.sources_order);
  const nb = useNumbering();
  const [q, setQ] = useState('');
  const [confirm, setConfirm] = useState<string | null>(null);
  const cited = useMemo(() => {
    const m = new Map<string, number>();
    ctx.editor?.state.doc.descendants((n) => { if (n.type.name === 'cite') for (const it of n.attrs.items || []) m.set(it.source, (m.get(it.source) || 0) + 1); return true; });
    return m;
  }, [nb, sources]);
  const rows = nb.sourceList.length === sources.length ? nb.sourceList : sources.map((s) => [s.id, srcText(s)] as [string, string]);
  const shown = rows.filter(([, t]) => !q || t.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="sources-panel" data-testid="sources-panel">
      <div className="panel-tools">
        <Btn icon="plus" label="Добавить" onClick={() => ctx.openDialog('sto.source', {})} testid="source-add" />
        <Btn icon="import" label="Импорт" title="BibTeX, RIS или готовый список по ГОСТ" onClick={() => ctx.openDialog('sto.sourceImport', {})} testid="source-import" />
        <Btn icon="cite" label="Сослаться" title="Ctrl+Shift+C или @ в тексте" onClick={() => ctx.run('refs.cite')} />
      </div>
      <div className="panel-sub muted small">{order === 'as-is' ? 'Порядок - как в библиотеке' : 'По алфавиту, иностранные в конце (4.13.4)'} ·
        <button type="button" className="link-btn" onClick={() => ctx.store.getState().setMeta((m) => ({ ...m, options: { ...m.options, sources_order: m.options.sources_order === 'as-is' ? 'alpha' : 'as-is' } }))}>изменить</button></div>
      {sources.length > 4 && <input className="search" placeholder="Поиск…" value={q} onChange={(e) => setQ(e.target.value)} />}
      {!sources.length && <div className="empty-card">
        <Icon name="library" size={28} />
        <div>Источников нет. Добавьте запись - номер и строку по ГОСТ Р 7.0.100 редактор поставит сам.</div>
        <div className="muted small">Можно вставить готовый список, BibTeX (Google Scholar, eLibrary) или RIS.</div>
      </div>}
      <ol className="source-list">
        {shown.map(([id, text]) => {
          const probs = sourceProblems.get(id) || [];
          const nCited = cited.get(id) || 0;
          return (
            <li key={id} className={'source-row' + (probs.length ? ' has-problem' : '')} data-testid="source-row"
              draggable onDragStart={(e) => { e.dataTransfer.setData('text/plain', `[${nb.sources.get(id)}]`); }}>
              <span className="source-num">{nb.sources.get(id) ?? '?'}</span>
              <span className="source-text">{text}
                <span className="source-meta">
                  {nCited ? <span className="badge-ok">{nCited} ссыл.</span> : <span className="badge-warn">нет ссылок</span>}
                  {probs.length > 0 && <span className="badge-warn" title={probs.join('\n')}>{probs[0]}{probs.length > 1 ? ` (+${probs.length - 1})` : ''}</span>}
                </span>
              </span>
              <span className="source-actions">
                {confirm === id ? <>
                  <button type="button" className="btn-mini btn-mini-danger" onClick={() => { ctx.store.getState().setSources((l) => l.filter((x) => x.id !== id)); setConfirm(null); }}>Удалить</button>
                  <button type="button" className="btn-mini" onClick={() => setConfirm(null)}>Нет</button>
                </> : <>
                  <button type="button" title="Вставить ссылку [N]" onClick={() => insertCite(ctx, [{ source: id }])}><Icon name="cite" size={14} /></button>
                  <button type="button" title="Изменить" onClick={() => ctx.openDialog('sto.source', { id })}><Icon name="rename" size={14} /></button>
                  <button type="button" title={nCited ? `Удалить (ссылок в тексте: ${nCited} - станут «[?]»)` : 'Удалить'} onClick={() => setConfirm(id)}><Icon name="trash" size={14} /></button>
                </>}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

// ------------------------------------------------------------------------------------------------ запись
export function SourceDialog({ ctx, props, close }: DialogProps) {
  const existing = ctx.store.getState().sources.find((s) => s.id === props.id) as AnySource | undefined;
  const [s, setS] = useState<AnySource>(existing ? { ...existing } : { id: newId().replace(/^n/, 's'), kind: (props.kind || 'book') as any, authors: [], ...(props.init || {}) });
  const [preview, setPreview] = useState('');
  const [probs, setProbs] = useState<string[]>([]);
  const [paste, setPaste] = useState('');
  const [manual, setManual] = useState(!!existing?.text);
  useEffect(() => {
    const t = window.setTimeout(() => {
      ctx.api.post<{ items: { text: string; problems: string[] }[] }>('/api/sources/format', { sources: [s], order: 'as-is' })
        .then((r) => { setPreview(r.items[0]?.text || ''); setProbs(r.items[0]?.problems || []); }).catch(() => setPreview(''));
    }, 200);
    return () => window.clearTimeout(t);
  }, [s]);
  const set = (k: string, v: unknown) => setS((cur) => {
    const next: AnySource = { ...cur, [k]: v };
    if (k === 'url' && v && !cur.accessed) next.accessed = todayRu();      // дата обращения - сегодня (4.13.6.9)
    return next;
  });
  const dup = useMemo(() => {
    const t = normTitle(s.title);
    return t.length > 6 ? ctx.store.getState().sources.find((x) => x.id !== s.id && normTitle(x.title) === t) : undefined;
  }, [s.title]);
  const parsePaste = async (text: string) => {
    if (!text.trim()) return;
    try {
      const r = await ctx.api.post<{ items: AnySource[] }>('/api/sources/parse', { text });
      const it = r.items[0];
      if (it) { setS({ ...it, id: s.id } as AnySource); setManual(it.kind === 'other'); ctx.toast(it.kind === 'other' ? 'Строка не разобрана - сохранена как есть' : 'Поля заполнены из строки - проверьте', it.kind === 'other' ? 'warn' : 'ok'); }
    } catch (e: any) { ctx.toast(e.message, 'error'); }
  };
  const save = () => {
    const rec = { ...s, formatted: preview, authors: (s.authors || []).map((a: string) => a.trim()).filter(Boolean) } as Source;
    if (!manual && s.kind !== 'other') delete (rec as any).text;
    ctx.store.getState().setSources((list) => (existing ? list.map((x) => (x.id === s.id ? rec : x)) : [...list, rec]));
    close(rec.id);
  };
  const fields = FIELDS[s.kind] || [];
  return (
    <Modal title={existing ? 'Источник' : 'Новый источник'} onClose={() => close()} width={780} testid="source-dialog" footer={<>
      {dup && <span className="hint-warn small">Похожий источник уже есть в библиотеке</span>}
      <span className="topbar-spacer" />
      <Btn label="Отмена" onClick={() => close()} />
      <Btn label={existing ? 'Сохранить' : 'Добавить'} primary onClick={save} testid="source-save" disabled={!preview && !s.text} />
    </>}>
      {!existing && (
        <div className="paste-line">
          <input placeholder="Вставьте готовую строку из списка литературы, BibTeX или RIS - поля заполнятся сами" value={paste}
            onChange={(e) => setPaste(e.target.value)} onPaste={(e) => { const t = e.clipboardData.getData('text'); window.setTimeout(() => void parsePaste(t), 0); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void parsePaste(paste); } }} data-testid="source-paste" />
          <Btn label="Разобрать" onClick={() => parsePaste(paste)} disabled={!paste.trim()} testid="source-parse" />
        </div>
      )}
      <div className="kind-row">
        <select value={s.kind} onChange={(e) => set('kind', e.target.value)} data-testid="source-kind">
          {KINDS.map(([k, t]) => <option key={k} value={k}>{t}</option>)}
        </select>
        <select value={s.lang || ''} onChange={(e) => set('lang', e.target.value || undefined)} title="Язык описания: иностранное - «p.», «Vol.», «[et al.]», «Accessed» (Прил. М.3.6)"
          data-testid="source-lang">
          <option value="">язык: авто</option><option value="ru">русское описание</option><option value="en">иностранное (p., Vol., Accessed)</option>
        </select>
        <div className="kind-chips">
          {KINDS.filter(([k]) => k !== 'other').map(([k, t, ic]) => (
            <button key={k} type="button" className={'chip' + (s.kind === k ? ' is-active' : '')} onClick={() => set('kind', k)} title={t}>
              <Icon name={ic} size={13} /> {t.split(',')[0]}</button>
          ))}
        </div>
      </div>
      <div className="source-preview sticky-preview" data-testid="source-preview"><b>В списке:</b> {preview || <span className="muted">заполните поля…</span>}
        {probs.length > 0 && !s.text && <div className="source-probs">{probs.map((p) => <div key={p} className="hint-warn small">• {p}</div>)}</div>}</div>
      <div className="form-grid">
        {!NO_AUTHORS.has(s.kind) && (
          <Field label="Авторы - каждый с новой строки: «Фамилия И. О.»" wide hint={(s.authors || []).filter((a: string) => a.trim()).length > 3 ? 'Четыре и более авторов: запись под заглавием, в сведениях - первые три [и др.] (4.13.6.1)' : undefined}>
            <textarea rows={Math.min(5, Math.max(2, (s.authors || []).length + 1))} value={(s.authors || []).join('\n')}
              onChange={(e) => set('authors', e.target.value.split('\n'))} placeholder={'Олифер В. Г.\nОлифер Н. А.'} data-testid="source-authors" />
          </Field>
        )}
        {fields.map(([k, label, ex]) => (
          <Field key={k} label={label} wide={WIDE.has(k)}>
            <input value={(s as any)[k] || ''} placeholder={ex} onChange={(e) => set(k, e.target.value)} data-testid={'source-' + k} />
          </Field>
        ))}
        <Field label="Примечание (необязательно)" wide>
          <input value={s.note || ''} onChange={(e) => set('note', e.target.value)} placeholder="Режим доступа: для авториз. пользователей" data-testid="source-note" />
        </Field>
        {(s.kind === 'other' || manual) ? (
          <Field label={s.kind === 'other' ? 'Описание по ГОСТ Р 7.0.100 (готовая строка)' : 'Своя строка (вместо автоматической)'} wide>
            <textarea rows={3} value={s.text || ''} onChange={(e) => set('text', e.target.value)} data-testid="source-text" />
          </Field>
        ) : (
          <button type="button" className="link-btn small" onClick={() => { setManual(true); set('text', preview); }}>Поправить строку вручную…</button>
        )}
      </div>
    </Modal>
  );
}

// ------------------------------------------------------------------------------------------------ импорт
export function SourceImportDialog({ ctx, close }: DialogProps) {
  const [text, setText] = useState('');
  const [items, setItems] = useState<(AnySource & { _sel: boolean; _text: string; _dup: boolean })[]>([]);
  const [fmt, setFmt] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const parse = async (t: string) => {
    if (!t.trim()) { setItems([]); return; }
    setBusy(true);
    try {
      const r = await ctx.api.post<{ format: string; items: AnySource[] }>('/api/sources/parse', { text: t });
      const withIds = r.items.map((it) => ({ ...it, id: newId().replace(/^n/, 's') }));
      const f = await ctx.api.post<{ items: { id: string; text: string }[] }>('/api/sources/format', { sources: withIds, order: 'as-is' });
      const byId = new Map(f.items.map((x) => [x.id, x.text]));
      const have = new Set(ctx.store.getState().sources.map((x) => normTitle(x.title)));
      setFmt(r.format);
      setItems(withIds.map((it) => ({ ...it, _text: byId.get(it.id) || '', _dup: !!it.title && have.has(normTitle(it.title)), _sel: !(it.title && have.has(normTitle(it.title))) })));
    } catch (e: any) { ctx.toast(e.message, 'error'); } finally { setBusy(false); }
  };
  useEffect(() => { const t = window.setTimeout(() => void parse(text), 350); return () => window.clearTimeout(t); }, [text]);
  const add = () => {
    const sel = items.filter((x) => x._sel).map(({ _sel, _text, _dup, ...rest }) => ({ ...rest, formatted: _text }) as Source);
    ctx.store.getState().setSources((l) => [...l, ...sel]);
    ctx.toast(`Добавлено источников: ${sel.length}`, 'ok');
    close(sel.length);
  };
  const n = items.filter((x) => x._sel).length;
  return (
    <Modal title="Импорт источников" onClose={() => close()} width={860} testid="source-import-dialog" footer={<>
      <span className="muted small">{fmt ? `Формат: ${{ bibtex: 'BibTeX', ris: 'RIS', gost: 'строки списка литературы', mixed: 'BibTeX/RIS и строки списка' }[fmt] || fmt}` : ''}</span>
      <span className="topbar-spacer" />
      <Btn label="Отмена" onClick={() => close()} />
      <Btn label={`Добавить ${n}`} primary onClick={add} disabled={!n || busy} testid="source-import-ok" />
    </>}>
      <p className="muted small">Вставьте список литературы (нумерованный или по строке на источник), записи BibTeX (Google Scholar, eLibrary, Zotero)
        или RIS - или выберите файл .bib / .ris / .txt. Строки будут переписаны по ГОСТ Р 7.0.100–2018.</p>
      <textarea className="import-text" rows={7} value={text} onChange={(e) => setText(e.target.value)} placeholder={'1. Олифер, В. Г. Компьютерные сети : учебник / В. Г. Олифер. – Санкт-Петербург : Питер, 2016. – 992 с.\n@article{key, author = {…}, title = {…}, …}'}
        data-testid="source-import-text" />
      <div className="panel-tools">
        <Btn icon="upload" label="Файл…" onClick={() => fileRef.current?.click()} />
        <input ref={fileRef} type="file" accept=".bib,.ris,.txt,.bibtex" hidden onChange={async (e) => {
          const f = e.target.files?.[0]; e.target.value = '';
          if (f) setText(await f.text());
        }} />
        {busy && <span className="muted"><Icon name="spinner" className="spin" /> Разбор…</span>}
      </div>
      {items.length > 0 && (
        <div className="import-list" data-testid="source-import-list">
          {items.map((it, i) => (
            <label key={it.id} className={'import-row' + (it._dup ? ' is-dup' : '')}>
              <input type="checkbox" checked={it._sel} onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, _sel: e.target.checked } : x)))} />
              <span className="import-kind">{KINDS.find((k) => k[0] === it.kind)?.[1] || it.kind}</span>
              <span className="import-text-row">{it._text}{it._dup && <span className="badge-warn">уже есть</span>}</span>
            </label>
          ))}
        </div>
      )}
    </Modal>
  );
}

// ------------------------------------------------------------------------------------------------ ссылка [N]
export function CiteDialog({ ctx, props, close }: DialogProps) {
  const sources = useApp((s) => s.sources);
  const nb = useNumbering();
  const initial: Record<string, string | undefined> = {};
  for (const it of (props.items || []) as { source: string; loc?: string }[]) initial[it.source] = it.loc || '';
  const [sel, setSel] = useState<Record<string, string | undefined>>(initial);
  const [q, setQ] = useState('');
  const [hi, setHi] = useState(0);
  const rows = useMemo(() => (nb.sourceList.length === sources.length ? nb.sourceList : sources.map((s) => [s.id, srcText(s)] as [string, string])), [nb, sources]);
  const shown = rows.filter(([, t]) => !q || t.toLowerCase().includes(q.toLowerCase()));
  const ok = () => {
    let items = Object.entries(sel).filter(([, v]) => v !== undefined).map(([source, loc]) => ({ source, loc: (loc || '').trim() }));
    if (!items.length && shown[hi]) items = [{ source: shown[hi][0], loc: '' }];
    items = items.map((it) => ({ ...it, loc: /^\d/.test(it.loc) ? 'с.' + NB + it.loc : it.loc }));
    if (items.length) {
      if (props.replacePos !== undefined && ctx.editor) {
        const p = props.replacePos as number;
        ctx.editor.view.dispatch(ctx.editor.state.tr.setNodeMarkup(p, undefined, { items }));
      } else insertCite(ctx, items);
    }
    close(items);
  };
  const toggle = (id: string) => setSel((cur) => ({ ...cur, [id]: cur[id] === undefined ? '' : undefined }));
  return (
    <Modal title="Ссылка на источник" onClose={() => close()} width={780} testid="cite-dialog" footer={<>
      <Btn icon="plus" label="Новый…" onClick={async () => {
        const id = await ctx.openDialog<string>('sto.source', {});
        if (id) setSel({ ...sel, [id]: '' });
      }} />
      <Btn icon="import" label="Импорт…" onClick={() => ctx.openDialog('sto.sourceImport', {})} />
      <span className="topbar-spacer" />
      <span className="muted small">Страница: «45» → [N, с. 45]</span>
      <Btn label="Отмена" onClick={() => close()} />
      <Btn label="Вставить" primary onClick={ok} disabled={!Object.values(sel).some((v) => v !== undefined) && !shown.length} testid="cite-ok" />
    </>}>
      <input className="search" placeholder="Поиск по автору, названию…" value={q} autoFocus data-testid="cite-search"
        onChange={(e) => { setQ(e.target.value); setHi(0); }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setHi((h) => Math.min(h + 1, shown.length - 1)); }
          if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
          if (e.key === ' ' && e.ctrlKey && shown[hi]) { e.preventDefault(); toggle(shown[hi][0]); }
          if (e.key === 'Enter') { e.preventDefault(); ok(); }
        }} />
      <div className="cite-list">
        {shown.map(([id, text], i) => (
          <label key={id} className={'cite-row' + (sel[id] !== undefined ? ' is-sel' : '') + (i === hi ? ' is-hi' : '')}>
            <input type="checkbox" checked={sel[id] !== undefined} onChange={() => toggle(id)} />
            <span className="source-num">{nb.sources.get(id)}</span>
            <span className="source-text">{text}</span>
            {sel[id] !== undefined && (
              <input className="cite-loc" placeholder="с. 45" value={sel[id] || ''} onClick={(e) => e.preventDefault()}
                onChange={(e) => setSel({ ...sel, [id]: e.target.value })} data-testid="cite-loc" />
            )}
          </label>
        ))}
        {!rows.length && <div className="muted pad">Библиотека пуста - нажмите «Новый…» или «Импорт…».</div>}
      </div>
    </Modal>
  );
}
